import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChromePicker } from 'react-color';

const BannerSettings = () => {
    const [bannerEnabled, setBannerEnabled] = useState(false);
    const [bannerTitle, setBannerTitle] = useState('');
    const [bannerBody, setBannerBody] = useState('');
    const [bannerOpacity, setBannerOpacity] = useState(1.0);
    const [bannerDay, setBannerDay] = useState('');
    const [bannerTimeStart, setBannerTimeStart] = useState('');
    const [bannerTimeEnd, setBannerTimeEnd] = useState('');
    const [bannerStreet, setBannerStreet] = useState('');
    const [bannerPostcode, setBannerPostcode] = useState('');
    const [bannerMapUrl, setBannerMapUrl] = useState('');
    const [bannerColor, setBannerColor] = useState('#000000'); // Default color is black
    const [bannerHtmlColor, setBannerHtmlColor] = useState('#000000'); // Default text color is black
    const [bannerBodyColor, setBannerBodyColor] = useState('#000000');
    const [buttonText, setButtonText] = useState('Opslaan'); // State for button text
    const [isSaved, setIsSaved] = useState(false); // State to track if settings are saved

    useEffect(() => {
        fetchBannerSettings();
    }, []);

    const formatDateToDDMMYYYY = (dateString) => {
        const dateObj = new Date(dateString);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = dateObj.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDateToYYYYMMDD = (dateString) => {
        return dateString.split('-').reverse().join('-');
    };

    const fetchBannerSettings = async () => {
        try {
            const response = await axios.get('/wp-json/heros-toolbox/v1/banner-settings');
            const { enabled, title, body, opacity, day, time, time_end, street, postcode, mapUrl, color, textColor, bodyColor } = response.data;

            // Convert DD-MM-YYYY back to YYYY-MM-DD for input compatibility
            const formattedDay = formatDateToYYYYMMDD(day);

            setBannerEnabled(enabled);
            setBannerTitle(title);
            setBannerBody(body);
            setBannerOpacity(opacity);
            setBannerDay(formattedDay);
            setBannerTimeStart(time);
            setBannerTimeEnd(time_end);
            setBannerStreet(street);
            setBannerPostcode(postcode);
            setBannerMapUrl(mapUrl);
            setBannerColor(color);
            setBannerHtmlColor(textColor);
            setBannerBodyColor(bodyColor);
        } catch (error) {
            console.error('Error fetching banner settings:', error);
        }
    };

    const saveBannerSettings = async () => {
        try {
            const formattedDay = formatDateToDDMMYYYY(bannerDay);

            await axios.post('/wp-json/heros-toolbox/v1/update-banner-settings', {
                enabled: bannerEnabled,
                title: bannerTitle,
                body: bannerBody,
                opacity: bannerOpacity,
                day: formattedDay, // Save the date in DD-MM-YYYY format
                time: bannerTimeStart,
                time_end: bannerTimeEnd,
                street: bannerStreet,
                postcode: bannerPostcode,
                mapUrl: bannerMapUrl,
                color: bannerColor,
                textColor: bannerHtmlColor,
                bodyColor: bannerBodyColor,
            });

            console.log('Banner settings saved successfully');
            setButtonText('Opgeslagen');
            setIsSaved(true); // Mark changes as saved

            // Call the clear cache function
            await clearCache();

            setTimeout(() => {
                setButtonText('Opslaan');
                setIsSaved(false);
            }, 3000);
        } catch (error) {
            console.error('Error saving banner settings:', error);
        }
    };

    // Function to clear cache
    const clearCache = async () => {
        try {
            await axios.post('/wp-json/custom/v1/clear-cache', {}, {
                headers: {
                    'X-WP-Nonce': heroToolbox.nonce, // Add the nonce to the request headers
                },
            });
            console.log('Cache cleared successfully');
        } catch (error) {
            console.error('Error clearing cache:', error);
        }
    };

    const handleTitleChange = (event) => {
        setBannerTitle(event.target.value);
    };

    const handleBodyChange = (event) => {
        setBannerBody(event.target.value);
    };

    const handleColorChange = (color) => {
        setBannerColor(color.hex);
    };

    const handleTextColorChange = (color) => {
        setBannerHtmlColor(color.hex);
    };

    const handleBodyColorChange = (color) => {
        setBannerBodyColor(color.hex);
    };

    const handleOpacityChange = (event) => {
        setBannerOpacity(parseFloat(event.target.value));
    };

    const handleDayChange = (event) => {
        setBannerDay(event.target.value);
    };

    const handleTimeStartChange = (event) => {
        setBannerTimeStart(event.target.value);
    };

    const handleTimeEndChange = (event) => {
        setBannerTimeEnd(event.target.value);
    }

    const handleStreetChange = (event) => {
        setBannerStreet(event.target.value);
    };

    const handlePostcodeChange = (event) => {
        setBannerPostcode(event.target.value);
    };

    const handleMapUrlChange = (event) => {
        setBannerMapUrl(event.target.value);
    };

    const toggleBanner = () => {
        setBannerEnabled(!bannerEnabled);
    };

    return (
        <div>
            <div className="banner-settings-wrapper">
                <h1>Algemene instellingen</h1>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_toggle">
                            <h5>Schakel banner in</h5>
                            <p>
                                <label htmlFor="bannerToggle">Gebruik deze knop om de banner in of uit te schakelen</label>
                            </p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="bannerToggle"
                                    checked={bannerEnabled}
                                    onChange={toggleBanner}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner color</h5>
                            <p>Beheer hier de kleur van de banner.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <ChromePicker
                                color={bannerColor}
                                onChange={handleColorChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner header color</h5>
                            <p>Beheer hier de kleur van de header in de banner.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <ChromePicker
                                color={bannerHtmlColor}
                                onChange={handleTextColorChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner body color</h5>
                            <p>Beheer hier de kleur van de body tekst in de banner.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <ChromePicker
                                color={bannerBodyColor}
                                onChange={handleBodyColorChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner opacity</h5>
                            <p>Beheer hier de doorzichtigheid van de banner op een schaal van 0 tot 1.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="number"
                                id="bannerOpacity"
                                step="0.1"
                                min="0"
                                max="1"
                                value={bannerOpacity}
                                onChange={handleOpacityChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="banner-settings-wrapper">
                <h1>Praktische informatie</h1>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner titel</h5>
                            <p>Voer hier de titel van de banner in met bijvoorbeeld de naam van de expositie.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <textarea
                                className="banner-settings-inner-content-details_input-text"
                                id="bannerTitle"
                                value={bannerTitle}
                                onChange={handleTitleChange}
                                rows={4}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner body</h5>
                            <p>Voer hier de body van de banner in met bijvoorbeeld een omschrijving van de expositie.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <textarea
                                className="banner-settings-inner-content-details_input-text"
                                id="bannerBody"
                                value={bannerBody}
                                onChange={handleBodyChange}
                                rows={4}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner datum</h5>
                            <p>Voer hier de dag van de banner in.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="date"
                                id="bannerDay"
                                value={bannerDay}
                                onChange={handleDayChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner tijd</h5>
                            <p>Voer hier de tijd van de banner in (bijv. 16:00).</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="time"
                                id="bannerTimeStart"
                                value={bannerTimeStart}
                                onChange={handleTimeStartChange}
                            />
                            <input
                                type="time"
                                id="bannerTimeEnd"
                                value={bannerTimeEnd}
                                onChange={handleTimeEndChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner straat</h5>
                            <p>Voer hier de straatnaam en het huisnummer in.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="text"
                                id="bannerStreet"
                                value={bannerStreet}
                                onChange={handleStreetChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner postcode</h5>
                            <p>Voer hier de postcode van de banner locatie in.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="text"
                                id="bannerPostcode"
                                value={bannerPostcode}
                                onChange={handlePostcodeChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="banner-settings-switch">
                    <div className="banner-settings-inner-content">
                        <div className="banner-settings-inner-content-details_input">
                            <h5>Banner map url</h5>
                            <p>Voer hier de Google Maps URL in.</p>
                        </div>
                        <div className="banner-settings-inner-content-toggle">
                            <input
                                type="url"
                                id="bannerMapUrl"
                                value={bannerMapUrl}
                                onChange={handleMapUrlChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="banner-settings-footer">
                <button onClick={saveBannerSettings} disabled={isSaved}>
                    {buttonText}
                </button>
            </div>
        </div>
    );
};

export default BannerSettings;